import { Game, GameChallenge } from '../types/game.types';
import { Subscription } from '../types/subscription.types';

export type DisplayState = 'playable' | 'locked' | 'display-only';

/**
 * Frontend Challenge Display Styling Service
 *
 * ⚠️ CRITICAL ARCHITECTURE RULE ⚠️
 * This file contains ONLY UI presentation logic. NO business logic.
 *
 * All business logic for challenge access is in the backend gameAccessService.
 * The backend determines displayState, and this frontend service only maps
 * displayState to UI styling decisions.
 *
 * Backend Authority: gameAccessService.ts
 * - Determines canPlay, displayState, and access reasons
 * - Single source of truth for all business rules
 * - Cannot be bypassed (server-side enforcement)
 *
 * Frontend Responsibility: challengeAccess.ts (this file)
 * - Maps backend displayState to UI styling
 * - NO subscription checks
 * - NO date comparisons
 * - NO business logic of any kind
 *
 * See: docs/planning/pr-plan-IMA-106-PR1.md Section 6 for architecture details
 */

/**
 * Gets display styling based on backend displayState
 *
 * This function ONLY handles UI presentation. All business logic is in the backend.
 *
 * @param displayState - Display state from backend gameAccessService
 * @param challengeType - Game challenge type for special UI handling (optional)
 * @returns Object with styling properties for UI components
 */
export function getChallengeDisplayStyling(
  displayState: DisplayState,
  challengeType?: string
) {

  // Auto-challenge display-only: Special pale blue styling
  if (challengeType === 'auto-challenge' && displayState === 'display-only') {
    return {
      showLockIcon: false,
      isClickable: false,
      styling: 'pale-blue', // Muted pale blue for auto-challenge past challenges
      clickAction: 'none' as const
    };
  }

  // Standard displayState mapping (no business logic)
  switch (displayState) {
    case 'display-only':
      return {
        showLockIcon: false,
        isClickable: false,
        styling: 'muted', // Muted/different color styling
        clickAction: 'none' as const
      };

    case 'playable':
      return {
        showLockIcon: false,
        isClickable: true,
        styling: 'normal', // Normal styling
        clickAction: 'open-game' as const
      };

    case 'locked':
      return {
        showLockIcon: true,
        isClickable: true,
        styling: 'normal', // Normal color but with lock icon
        clickAction: 'show-subscription-cta' as const
      };

    default:
      // Fallback for unknown display state
      return {
        showLockIcon: false,
        isClickable: false,
        styling: 'normal',
        clickAction: 'none' as const
      };
  }
}

/**
 * Gets user-friendly message based on displayState
 *
 * @param displayState - Display state from backend gameAccessService
 * @param gameName - Name of the game for contextual messages
 * @returns Human-readable message explaining the access state
 */
export function getChallengeAccessMessage(
  displayState: DisplayState,
  gameName: string
): string {

  switch (displayState) {
    case 'playable':
      return `Play ${gameName} challenge`;

    case 'display-only':
      return `View past ${gameName} challenge`;

    case 'locked':
      return `Premium required for ${gameName} challenge`;

    default:
      return `${gameName} challenge`;
  }
}

/**
 * Legacy compatibility function for existing code
 *
 * @deprecated Backend now provides displayState directly. Use backend data instead.
 * @param _challenge - GameChallenge object (unused)
 * @param _game - Game object (unused)
 * @param _subscription - Subscription object (unused)
 * @returns boolean indicating if challenge can be accessed
 */
export function canAccessChallenge(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _challenge: GameChallenge,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _game: Game,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _subscription: Subscription | null
): boolean {
  console.warn('canAccessChallenge() is deprecated. Use backend displayState instead.');

  // Simplified legacy logic - always allow for backward compatibility
  // Real access control is enforced by backend
  return true;
}

/**
 * Legacy compatibility function for existing code
 *
 * @deprecated Backend now provides displayState and reason. Use backend data instead.
 * @param _challenge - The locked challenge (unused)
 * @param game - The game configuration
 * @returns A message explaining why the challenge is locked
 */
export function getLockedMessage(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _challenge: GameChallenge,
  game: Game
): string {
  console.warn('getLockedMessage() is deprecated. Use getChallengeAccessMessage() instead.');

  const periodName = game.challengeFrequency === 'daily' ? 'day' :
                     game.challengeFrequency === 'weekly' ? 'week' : 'month';

  return `Premium access required for previous ${periodName}'s challenges`;
}

/**
 * Legacy compatibility function for existing code
 *
 * @deprecated Backend now determines timing. Use backend displayState instead.
 * @param challenge - The challenge to check
 * @param game - The game configuration
 * @returns true if the challenge is in the current period
 */
export function isCurrentPeriod(challenge: GameChallenge, game: Game): boolean {
  console.warn('isCurrentPeriod() is deprecated. Use backend displayState instead.');

  // Simplified legacy logic - check if published today
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
  const challengeDate = challenge.publishDate.split('T')[0];
  return challengeDate === today;
}

/**
 * Legacy compatibility function for existing code
 *
 * @deprecated Use backend metadata for "new" indicators
 * @param challenge - The challenge to check
 * @returns true if the challenge should show a "New" badge
 */
export function isNewChallenge(challenge: GameChallenge): boolean {
  const now = new Date();
  const challengeDate = new Date(challenge.publishDate);
  const hoursSincePublish = (now.getTime() - challengeDate.getTime()) / (1000 * 60 * 60);

  // Show "New" badge for challenges published in the last 24 hours
  return hoursSincePublish <= 24;
}
